import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import type { GameStatistics } from '../../types/game';
import {
  OneBrainMaze,
  OneBrainControls,
  type OneBrainDirection,
  type OneBrainTeamState,
} from './oneBrain';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

interface OneBrainQuestion {
  questionId: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  teamId: 'A' | 'B';
  title: string;
  maze: string[];
  controls: OneBrainDirection[];
  teamState?: OneBrainTeamState | null;
  teamStates?: Record<'A' | 'B', OneBrainTeamState | null>;
  finished?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
}

interface OneBrainPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isOneBrain?: boolean;
      maze?: string[];
      title?: string;
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const OneBrainPlayerDisplay: React.FC<OneBrainPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [obQuestion, setObQuestion] = useState<OneBrainQuestion | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
        if (me.teamId) myTeamIdRef.current = me.teamId;
      }
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setObQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setObQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      currentQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const handleQuestion = (data: OneBrainQuestion) => {
      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setObQuestion(data);
      setTimeRemaining(data.timeLimit);
    };

    const handleState = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      state: OneBrainTeamState;
      teamStates: Record<'A' | 'B', OneBrainTeamState | null>;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setObQuestion((prev) => {
        if (!prev) return prev;
        const mine = data.teamStates[prev.teamId];
        return {
          ...prev,
          teamState: mine,
          teamStates: data.teamStates,
          finished: mine?.finished ?? prev.finished,
        };
      });
    };

    const handleFinished = (data: { questionId: string; teamId: 'A' | 'B' }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setObQuestion((prev) => {
        if (!prev || prev.teamId !== data.teamId) return prev;
        return { ...prev, finished: true };
      });
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setObQuestion(null);
      currentQuestionIdRef.current = undefined;
    };

    socket.on('one-brain-question', handleQuestion);
    socket.on('one-brain-state', handleState);
    socket.on('one-brain-team-finished', handleFinished);
    socket.on('game-started', handleGameStarted);

    if (roomState.currentQuestion?.isOneBrain) {
      const qId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      if (qId) {
        socket.emit('get-one-brain-state', { roomName: roomState.roomName, questionId: qId });
      }
    }

    return () => {
      socket.off('one-brain-question', handleQuestion);
      socket.off('one-brain-state', handleState);
      socket.off('one-brain-team-finished', handleFinished);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, roomState.roomName, roomState.currentQuestion]);

  useEffect(() => {
    if (!obQuestion || obQuestion.finished) return;
    const interval = setInterval(() => {
      const remaining = obQuestion.timeLimit - (Date.now() - obQuestion.startTime);
      setTimeRemaining(Math.max(0, remaining));
    }, 100);
    return () => clearInterval(interval);
  }, [obQuestion]);

  const handleMove = (direction: OneBrainDirection) => {
    if (!obQuestion || obQuestion.finished || timeRemaining <= 0) return;
    socket.emit('one-brain-move', {
      roomName: roomState.roomName,
      questionId: obQuestion.questionId,
      direction,
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setObQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameIcon="🧠"
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!obQuestion) {
    if (roomState.currentQuestion?.isOneBrain) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header
          leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.oneBrain.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[obQuestion.teamId]?.score ?? 0;

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
        roomCode={roomState.roomName}
      />
      <div className="game-player-wrapper" style={{ padding: '1rem', paddingTop: '1.25rem', width: '100%' }}>
        <div style={{ maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.85rem', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', width: '100%' }}>
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
              {t('gameplay.oneBrain.round', {
                current: obQuestion.questionNumber ?? 1,
                total: obQuestion.totalQuestions ?? 1,
              })}
            </span>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 4 }}>{obQuestion.title}</div>
          </div>

          <TimerBar timeRemaining={timeRemaining} timeLimit={obQuestion.timeLimit} />

          <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>
            {t('gameplay.oneBrain.teamScore', { team: obQuestion.teamId, score: teamScore })}
          </div>

          <OneBrainMaze
            maze={obQuestion.maze}
            teamStates={obQuestion.teamStates}
            focusTeam={obQuestion.teamId}
            compact
          />

          {obQuestion.finished ? (
            <p style={{ margin: 0, fontWeight: 700, color: '#22C55E', textAlign: 'center' }}>
              {t('gameplay.oneBrain.finished')}
            </p>
          ) : (
            <OneBrainControls
              controls={obQuestion.controls}
              disabled={timeRemaining <= 0}
              onMove={handleMove}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OneBrainPlayerDisplay;
